import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { usePageHeader } from "../layout";

const numberFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const decimalFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const percentFormatter = new Intl.NumberFormat("pt-BR", {
  style: "percent",
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export function shortMonth(competencia) {
  if (!competencia) return "";
  const [year, month] = String(competencia).split("-");
  return year && month ? `${MONTHS[Number(month) - 1]?.slice(0, 3) || month}/${year}` : competencia;
}

export function money(value, { signed = false } = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const rendered = currencyFormatter.format(Math.abs(numeric));
  if (signed && numeric > 0) return `+${rendered}`;
  return numeric < 0 ? `−${rendered}` : rendered;
}

export function quantity(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numberFormatter.format(numeric) : "—";
}

export function decimal(value, digits = 2) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  if (digits === 0) return numberFormatter.format(numeric);
  return decimalFormatter.format(numeric);
}

export function percent(value, { signed = false } = {}) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  const rendered = percentFormatter.format(Math.abs(numeric));
  if (signed && numeric > 0) return `+${rendered}`;
  return numeric < 0 ? `−${rendered}` : rendered;
}

export function dateLabel(value, withTime = false) {
  if (!value) return "—";
  const date = new Date(/(?:Z|[+-]\d\d:?\d\d)$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("pt-BR", withTime
    ? { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "numeric" });
}

function messageFromResponse(data, fallback) {
  const detail = data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || String(item)).join("; ");
  return detail || data?.message || fallback;
}

/** Small API client shared by all screens. Cookies support the SSO flow; the token keeps local login working. */
export async function api(path, { method = "GET", body, params, signal } = {}) {
  const base = window.localStorage.getItem("allstrategy_api") || "";
  const token = window.localStorage.getItem("allstrategy_token");
  const url = new URL(`${base}${path}`, window.location.origin);
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, String(value));
  });
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const isFormData = body instanceof FormData;
  if (body !== undefined && !isFormData) headers["Content-Type"] = "application/json";
  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : (isFormData ? body : JSON.stringify(body)),
    credentials: "include",
    signal,
  });
  if (response.status === 401) {
    window.localStorage.removeItem("allstrategy_token");
    if (!window.location.pathname.startsWith("/login")) window.location.assign("/login");
    throw new Error("Sua sessão expirou. Entre novamente para continuar.");
  }
  if (response.status === 204) return null;
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(messageFromResponse(data, `Não foi possível concluir a solicitação (${response.status}).`));
  return data;
}

export function useLoader(loader, deps = []) {
  const [state, setState] = useState({ data: null, error: "", loading: true });
  const reload = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await loader();
      setState({ data, error: "", loading: false });
      return data;
    } catch (error) {
      if (error?.name === "AbortError") return undefined;
      setState({ data: null, error: error?.message || "Não foi possível carregar os dados.", loading: false });
      return undefined;
    }
  // The consumer defines the explicit dependencies, which is clearer than reloading for a new callback identity.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
}

export function useFlash() {
  const [flash, setFlash] = useState(null);
  useEffect(() => {
    if (!flash) return undefined;
    const timer = window.setTimeout(() => setFlash(null), 4200);
    return () => window.clearTimeout(timer);
  }, [flash]);
  const notify = useCallback((message, tone = "success") => setFlash({ message, tone }), []);
  const Flash = useCallback(() => flash ? <div className={`flash flash-${flash.tone}`} role="status">{flash.message}</div> : null, [flash]);
  return { notify, Flash };
}

export function PageIntro({ eyebrow, title, description, actions }) {
  const pageHeader = usePageHeader();
  useLayoutEffect(() => {
    if (!pageHeader) return undefined;
    pageHeader.setPageHeader({ eyebrow, title, description, actions });
    return () => pageHeader.setPageHeader(null);
  }, [pageHeader, eyebrow, title, description, actions]);
  return null;
}

export function Card({ children, className = "", title, action, subtitle }) {
  return <section className={`card ${className}`}>
    {(title || action || subtitle) && <div className="card-header">
      <div>{title && <h2>{title}</h2>}{subtitle && <p>{subtitle}</p>}</div>
      {action}
    </div>}
    {children}
  </section>;
}

export function Metric({ label, value, trend, tone = "neutral", helper, icon }) {
  return <article className={`metric-card tone-${tone}`}>
    <div className="metric-topline"><span>{label}</span>{icon && <span className="metric-icon" aria-hidden="true">{icon}</span>}</div>
    <strong>{value}</strong>
    {(trend || helper) && <p>{trend && <span className={`trend ${trend.value < 0 ? "negative" : "positive"}`}>{trend.label}</span>}{helper && <span>{helper}</span>}</p>}
  </article>;
}

export function Loading({ label = "Carregando dados…" }) {
  return <div className="loading-state"><span className="loading-spinner" aria-hidden="true" />{label}</div>;
}

export function ErrorState({ message, onRetry }) {
  return <div className="empty-state error-state"><strong>Não foi possível carregar esta visão</strong><p>{message}</p>{onRetry && <button className="button secondary" type="button" onClick={onRetry}>Tentar novamente</button>}</div>;
}

export function Empty({ title = "Nada por aqui ainda", detail, action }) {
  return <div className="empty-state"><strong>{title}</strong>{detail && <p>{detail}</p>}{action}</div>;
}

export function Modal({ title, children, onClose, footer, wide = false }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
    <section className={`modal ${wide ? "modal-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
      <header><h2>{title}</h2><button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">×</button></header>
      <div className="modal-content">{children}</div>
      {footer && <footer>{footer}</footer>}
    </section>
  </div>;
}

export function Tabs({ items, current, onChange, ariaLabel = "Alternar visão" }) {
  return <div className="tabs" role="tablist" aria-label={ariaLabel}>
    {items.map((item) => <button key={item.id} type="button" role="tab" aria-selected={current === item.id}
      className={current === item.id ? "active" : ""} onClick={() => onChange(item.id)}>{item.label}</button>)}
  </div>;
}

export function Field({ label, children, hint, className = "" }) {
  return <label className={`field ${className}`}><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export function SelectMany({ label, options, value, onChange, emptyLabel = "Todas" }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const selected = new Set(value);
  useEffect(() => {
    const closeWhenClickingAway = (event) => { if (root.current && !root.current.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", closeWhenClickingAway);
    return () => document.removeEventListener("mousedown", closeWhenClickingAway);
  }, []);
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange([...next]);
  };
  return <div className={`multi-select ${open ? "is-open" : ""}`} ref={root}>
    <button type="button" className="multi-select-trigger" onClick={() => setOpen((current) => !current)} aria-expanded={open}><span>{label}</span><strong>{value.length ? `${value.length} selecionada${value.length > 1 ? "s" : ""}` : emptyLabel}</strong><i aria-hidden="true">⌄</i></button>
    {open && <div className="multi-select-menu">
      <button type="button" className="text-button" onClick={() => { onChange([]); setOpen(false); }}>Limpar seleção</button>
      {options.map((option) => <label key={option.id}><input type="checkbox" checked={selected.has(option.id)} onChange={() => toggle(option.id)} />{option.label}</label>)}
    </div>}
  </div>;
}

export function Pagination({ page, pages, onChange }) {
  if (!pages || pages <= 1) return null;
  return <nav className="pagination" aria-label="Paginação">
    <button className="button ghost" type="button" disabled={page <= 1} onClick={() => onChange(page - 1)}>Anterior</button>
    <span>Página {page} de {pages}</span>
    <button className="button ghost" type="button" disabled={page >= pages} onClick={() => onChange(page + 1)}>Próxima</button>
  </nav>;
}

export function stableArray(value) {
  return Array.isArray(value) ? value : [];
}

export function flattenTree(nodes, result = []) {
  stableArray(nodes).forEach((node) => {
    result.push(node);
    flattenTree(node.filhos, result);
  });
  return result;
}

export function useDebounced(value, delay = 280) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function useDerived(source, transform) {
  return useMemo(() => transform(source), [source, transform]);
}
